// AuthCallback — one-shot handler for the OAuth return trip.
//
// Emergent auth lands users at `{origin}/sessions#session_id=<...>` after
// Google auth completes. This component:
//   1. Reads session_id from the URL hash (synchronously via useRef guard).
//   2. POSTs it to /api/auth/session → backend sets httpOnly cookie.
//   3. Refreshes AuthContext so <Shell> renders with the user's identity.
//   4. Strips the hash and navigates to /sessions.
//
// StrictMode-safe: uses useRef so the effect only runs once even under
// double-invocation in dev.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Auth } from "../lib/auth";
import { useAuth } from "../context/AuthContext";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      setErr("Missing session_id — start over from the login page.");
      return;
    }
    const sessionId = decodeURIComponent(m[1]);
    (async () => {
      try {
        await Auth.exchangeSession(sessionId);
        // Strip the hash so a refresh doesn't retry the exchange.
        window.history.replaceState({}, "", window.location.pathname);
        await refresh();
        toast.success("SIGNED IN");
        navigate(Auth.consumePostLogin(), { replace: true });
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Auth failed";
        setErr(String(detail).slice(0, 200));
      }
    })();
  }, [navigate, refresh]);

  return (
    <div className="min-h-[80dvh] grid place-items-center px-4" data-testid="auth-callback">
      <div className="border-2 border-black p-6 max-w-md w-full space-y-3 bg-white">
        <div className="font-display font-black tracking-tighter text-2xl">
          {err ? "AUTH FAILED" : "SIGNING YOU IN…"}
        </div>
        {err && (
          <>
            <div className="text-sm font-mono text-red-600" data-testid="auth-callback-error">
              {err}
            </div>
            <button
              className="btn-flat text-xs"
              data-testid="auth-callback-retry"
              onClick={() => navigate("/login", { replace: true })}
            >
              BACK TO LOGIN
            </button>
          </>
        )}
        {!err && (
          <div className="label-mono text-xs text-neutral-500">
            Exchanging Google session · establishing cookie · redirecting…
          </div>
        )}
      </div>
    </div>
  );
}
